import i18next from "i18next";

import { relativeDate } from "./dates";

export default class Day implements DayInterface {
  public day: number;
  public month: number;
  public year: number;

  constructor(date: `${number}-${number}-${number}` | Date | Day = new Date()) {
    if (typeof date === "string") {
      const [year, month, day] = date.split("-").map(Number);
      this.year = year!;
      this.month = month!;
      this.day = day!;
    } else if (date instanceof Day) {
      this.year = date.year;
      this.month = date.month;
      this.day = date.day;
    } else {
      this.year = date.getUTCFullYear();
      this.month = date.getUTCMonth() + 1;
      this.day = date.getUTCDate();
    }
  }

  /**
   * Builds a `Day` from a `Date`'s local calendar day. Use for picker values (local midnight),
   * where the constructor's UTC read would land on the previous day east of UTC.
   */
  static fromLocalDate(date: Date): Day {
    return new Day(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`);
  }

  static isValidDateString(date: null | string | undefined): date is `${number}-${number}-${number}` {
    if (typeof date !== "string") return false;
    return /[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}/.test(date);
  }

  public add(count: number, unit: "days" | "months" | "years"): DayInterface {
    return new Day(
      relativeDate(
        {
          [unit]: count,
        },
        this.beginningOfDay(),
      ),
    );
  }

  public at(hour: number, minute: number, second: number = 0) {
    return new Date(this.year, this.month - 1, this.day, hour, minute, second);
  }

  public atTimezone(timeZone: string, hour: number, minute: number, second: number = 0): Date {
    const date = new Date(Date.UTC(this.year, this.month - 1, this.day, hour, minute, second));

    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(date.toLocaleString("en-US", { timeZone: timeZone }));
    const offset = utcDate.getTime() - tzDate.getTime();

    date.setTime(date.getTime() + offset);

    return date;
  }

  public beginningOfDay() {
    return new Date(this.year, this.month - 1, this.day);
  }

  public beginningOfMonth() {
    return new Date(this.year, this.month - 1, 1);
  }

  public beginningOfQuarter() {
    const date = this.beginningOfMonth();

    const monthsToSubtract = date.getMonth() % 3;

    return relativeDate({ months: -monthsToSubtract }, date);
  }

  public beginningOfWeek() {
    const date = this.beginningOfDay();
    date.setDate(date.getDate() - date.getDay() + 1);
    return date;
  }

  public beginningOfYear() {
    return new Date(this.year, 0, 1);
  }

  public daysTo(date: DayInterface) {
    return Math.round(
      (date.utcBeginningOfDay().getTime() - this.utcBeginningOfDay().getTime()) / (1000 * 60 * 60 * 24),
    );
  }

  public endOfDay() {
    return new Date(this.year, this.month - 1, this.day, 23, 59, 59, 999);
  }

  public endOfMonth() {
    return new Date(this.year, this.month, 1);
  }

  public endOfQuarter() {
    const date = this.beginningOfMonth();

    const monthsToAdd = 3 - (date.getMonth() % 3);

    return relativeDate({ months: monthsToAdd }, date);
  }

  public endOfWeek() {
    const date = this.endOfDay();
    date.setDate(date.getDate() + (7 - date.getDay()));
    return date;
  }

  public endOfYear() {
    return new Date(this.year + 1, 0, 1);
  }

  public getTime() {
    return this.beginningOfDay().getTime();
  }

  /**
   * Returns the start of the day in global time.
   * The start of the day is defined as 12am in UTC+14:00.
   *
   * @returns {Date} A Date object representing the start of the day.
   */
  public globalBeginningOfDay() {
    return new Date(Date.UTC(this.year, this.month - 1, this.day) - 12 * 60 * 60 * 1000);
  }

  /**
   * Returns the end of the day in global time.
   * The end of the day is defined as midnight in UTC−12:00.
   *
   * @returns {Date} A Date object representing the end of the day.
   */
  public globalEndOfDay() {
    return new Date(Date.UTC(this.year, this.month - 1, this.day) + 38 * 60 * 60 * 1000 - 1);
  }

  public isAfter(date: Day) {
    return this.getTime() > date.getTime();
  }

  public isBefore(date: Day) {
    return this.getTime() < date.getTime();
  }

  public isSameDay(date: Day) {
    return this.toString() === date.toString();
  }

  public nextWeekday(): Day {
    let result = this.add(1, "days");
    let dayOfWeek = result.beginningOfDay().getDay();

    if (dayOfWeek === 6) result = result.add(2, "days");
    else if (dayOfWeek === 0) result = result.add(1, "days");

    return result;
  }

  public onWeekend(): boolean {
    const dayOfWeek = this.beginningOfDay().getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  public prevWeekday(): Day {
    let result = this.subtract(1, "days");
    let dayOfWeek = result.beginningOfDay().getDay();

    if (dayOfWeek === 6) result = result.subtract(1, "days");
    else if (dayOfWeek === 0) result = result.subtract(2, "days");

    return result;
  }

  public subtract(count: number, unit: "days" | "months" | "years") {
    return this.add(-count, unit);
  }

  public toJSON() {
    return this.toString();
  }

  public toLocaleDateString(locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions | undefined) {
    return this.beginningOfDay().toLocaleDateString(locales ?? i18next.languages[0] ?? "en-US", options);
  }

  public toString(): `${number}-${number}-${number}` {
    let month = String(this.month);
    if (month.length === 1) month = `0${month}`;

    let day = String(this.day);
    if (day.length === 1) day = `0${day}`;

    return `${this.year}-${month}-${day}` as `${number}-${number}-${number}`;
  }

  public utcBeginningOfDay() {
    return new Date(Date.UTC(this.year, this.month - 1, this.day));
  }

  public utcEndOfDay() {
    return new Date(Date.UTC(this.year, this.month - 1, this.day, 23, 59, 59, 999));
  }

  public valueOf() {
    return this.getTime();
  }
}
